import Field, {type FieldType} from '@packages/systems/dynamo/records/Field';

import {
  getCartItemsSkuField,
  getCartItemsProductField,
  getProductNameField,
  getSkuImageField,
  getSkuPriceField,
  buildProductItemRefField,
  buildSkuItemRefField,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';

import {
  NODE_NAME_COMMERCE_ADD_TO_CART_QUANTITY_INPUT,
  CHECKOUT_ERRORS,
  ADD_TO_CART_ERRORS,
  ADD_TO_CART_STATES,
  DATA_ATTR_NODE_TYPE,
  MEMBERSHIP_PRODUCT_TYPE,
  QUICK_CHECKOUT_STATES,
  SHIPPING_METHODS_STATES,
  LOADING_TEXT,
  CHECKOUT_PLACE_ORDER_LOADING_TEXT_DEFAULT,
  BILLING_ADDRESS_TOGGLE_KEY,
  ORDER_TYPE,
  PREVIEW_ITEMS_DEFAULT,
  CHECKOUT_STATES,
} from '@packages/systems/commerce/constants';
import {type PageRecordType} from '@packages/systems/core/records/PageRecord';
import {type CollectionStateType as CollectionState} from '@packages/systems/core/records/CollectionStateType';
import type {BetaFeatureFlags} from '@packages/systems/core-constants';

import {
  Collection,
  type Collection as BackendCollection,
  type Field as BackendField,
} from '@packages/shared/pageTemplates/ecommerce';

import {Plugins, Component} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';

import {
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  EEnum,
  EList,
  ELiteral,
  type Expression,
} from '@packages/systems/wfdl-ast';

export const commerceAtom = (name: string): Plugins.QualifiedElement =>
  qualify(Plugins.name('Commerce'), Component.name(name));

export const isOrderConfirmationPage = (page: PageRecordType) =>
  page.utilKey === 'ecommerce-confirmation';
export const isCheckoutPage = (page: PageRecordType) =>
  page.utilKey === 'ecommerce-checkout';
export const isPaypalCheckoutPage = (page: PageRecordType) =>
  page.utilKey === 'ecommerce-paypal-checkout';
export const isCheckoutRelatedPage = (page: PageRecordType) =>
  isCheckoutPage(page) ||
  isOrderConfirmationPage(page) ||
  isPaypalCheckoutPage(page);

export type BindingJson = {
  id: string;
  slug: string;
  type: string;
};

type AnyField = FieldType | BackendField;

const toBindingObject = (fieldRecord?: AnyField | null): BindingJson | null => {
  if (fieldRecord != null) {
    if (fieldRecord instanceof Field) {
      const {id, slug, type} = fieldRecord.toJS();
      return {id, slug, type};
    } else {
      const {id, slug, type} = fieldRecord;
      return {id, slug, type};
    }
  }

  return null;
};

export const buildAddToCartForDefaultSku = (defaultSkuFieldId: string) =>
  EElement({
    id: 'Commerce Add To Cart Wrapper',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceAddToCartWrapper')
    ),
    data: ERecord({
      tag: EText('div'),
      temp: ERecord({
        state: EEnum(ADD_TO_CART_STATES.DEFAULT),
      }),
      commerce: ERecord({
        type: EText('add-to-cart-wrapper'),
        quantityEnabled: EBoolean(true),
      }),
      showAddToCartButton: EBoolean(true),
      showBuyNowButton: EBoolean(true),
      note: ELiteral({
        name: qualify(Plugins.name('Commerce'), typeName('DismissibleNote')),
        value: false,
      }),
      children: EList([
        EElement({
          id: 'Commerce Add To Cart Form',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceAddToCartForm')
          ),
          data: ERecord({
            commerce: ERecord({
              type: EText('add-to-cart-form'),
            }),
            dyn: ERecord({
              bind: ERecord({
                skuId: EList([
                  ERecord({
                    id: EText(defaultSkuFieldId),
                    slug: EText('default-sku'),
                    type: EText('ItemRef'),
                  }),
                  ERecord({
                    id: EText('id'),
                    slug: EText('id'),
                    type: EText('PlainText'),
                  }),
                ]),
              }),
            }),
            children: EList([
              EElement({
                id: 'Commerce Add To Cart Option List With Selector Types',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceAddToCartOptionListWithSelectorTypes')
                ),
                data: ERecord({
                  commerce: ERecord({
                    type: EText('add-to-cart-option-list'),
                  }),
                  source: ELiteral({
                    name: qualify(
                      Plugins.name('Commerce'),
                      typeName('LockedDynSource')
                    ),
                    value: '',
                  }),
                  preselectDefaultVariant: EBoolean(false),
                  selectorType: EEnum('select'),
                  children: EList([
                    EElement({
                      id: 'Commerce Add To Cart Option',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceAddToCartOption')
                      ),
                      data: ERecord({
                        commerce: ERecord({
                          type: EText('add-to-cart-option'),
                        }),
                        children: EList([
                          EElement({
                            id: 'Commerce Add To Cart Option Label',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceAddToCartOptionLabel')
                            ),
                            data: ERecord({
                              tag: EText('label'),
                              dyn: ERecord({
                                bind: ERecord({
                                  innerHTML: EList([
                                    ERecord({
                                      id: EText('id'),
                                      slug: EText('name'),
                                      type: EText('PlainText'),
                                    }),
                                  ]),
                                }),
                              }),
                              children: EList([]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Add To Cart Option Select',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceAddToCartOptionSelect')
                            ),
                            data: ERecord({
                              editable: EBoolean(true),
                              defaultText: EText('Select'),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Add To Cart Option Pill Group',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceAddToCartOptionPillGroup')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Add To Cart Option Pill',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceAddToCartOptionPill'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Add To Cart Wrapper Block',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  id: EText('id'),
                                                  slug: EText('name'),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Form Block Label',
                type: qualify(
                  Plugins.name('Form'),
                  Component.name('FormBlockLabel')
                ),
                data: ERecord({
                  attr: ERecord({
                    for: EText(`quantity-${defaultSkuFieldId}`),
                  }),
                  form: ERecord({
                    type: EText('label'),
                    passwordPage: EBoolean(false),
                  }),
                  dyn: ERecord({
                    condition: ERecord({
                      class: ERecord({
                        false: ERecord({
                          'w-condition-invisible': ERecord({
                            fields: ERecord({
                              'ec-product-type': ERecord({
                                ne: EText(MEMBERSHIP_PRODUCT_TYPE.id),
                              }),
                            }),
                          }),
                        }),
                      }),
                    }),
                  }),
                  children: EList([
                    EElement({
                      id: 'Commerce Add To Cart Wrapper String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Quantity'),
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Add To Cart Quantity Input',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceAddToCartQuantityInput')
                ),
                data: ERecord({
                  attr: ERecord({
                    id: EText(`quantity-${defaultSkuFieldId}`),
                    name: EText(NODE_NAME_COMMERCE_ADD_TO_CART_QUANTITY_INPUT),
                    type: EText('number'),
                    min: EText('1'),
                    value: EText('1'),
                  }),
                  commerce: ERecord({
                    type: EText('add-to-cart-quantity-input'),
                  }),
                  dyn: ERecord({
                    condition: ERecord({
                      class: ERecord({
                        false: ERecord({
                          'w-condition-invisible': ERecord({
                            fields: ERecord({
                              'ec-product-type': ERecord({
                                ne: EText(MEMBERSHIP_PRODUCT_TYPE.id),
                              }),
                            }),
                          }),
                        }),
                      }),
                    }),
                  }),
                }),
              }),
              EElement({
                id: 'Commerce Add To Cart Button',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceAddToCartButton')
                ),
                data: ERecord({
                  temp: ERecord({
                    index: ENumber(0),
                  }),
                  attr: ERecord({
                    type: EText('submit'),
                  }),
                  texts: EList([
                    ERecord({
                      id: EText('COMMERCE_ADD_TO_CART_BUTTON_DEFAULT'),
                      name: EText('Default text'),
                      copy: EText('Add to Cart'),
                      category: EText('Button label'),
                    }),
                    ERecord({
                      id: EText('COMMERCE_ADD_TO_CART_BUTTON_WAITING'),
                      name: EText('Waiting text'),
                      copy: EText('Adding to cart...'),
                      category: EText('Button label'),
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Buy Now Button',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceBuyNowButton')
                ),
                data: ERecord({
                  temp: ERecord({
                    index: ENumber(0),
                  }),
                  texts: EList([
                    ERecord({
                      id: EText('COMMERCE_BUY_NOW_BUTTON_DEFAULT'),
                      name: EText('Default text'),
                      copy: EText('Buy now'),
                      category: EText('Button label'),
                    }),
                    ERecord({
                      id: EText('SUBSCRIPTION_BUTTON_DEFAULT'),
                      name: EText('Subscription text'),
                      copy: EText('Subscribe now'),
                      category: EText('Button label'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Add To Cart Out Of Stock',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceAddToCartOutOfStock')
          ),
          data: ERecord({
            tag: EEnum('div'),
            commerce: ERecord({
              type: EText('add-to-cart-out-of-stock'),
            }),
            children: EList([
              EElement({
                id: 'Commerce Add To Cart Wrapper Block 2',
                type: qualify(Plugins.name('Basic'), Component.name('Block')),
                data: ERecord({
                  tag: EEnum('div'),
                  text: EBoolean(true),
                  children: EList([
                    EElement({
                      id: 'Commerce Add To Cart Wrapper String 2',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('This product is out of stock.'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Add To Cart Error',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceAddToCartError')
          ),
          data: ERecord({
            attr: ERecord({
              'aria-live': EText('assertive'),
            }),
            tag: EEnum('div'),
            commerce: ERecord({
              type: EText('add-to-cart-error'),
            }),
            children: EList([
              EElement({
                id: 'Commerce Add To Cart Error Msg',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceAddToCartErrorMsg')
                ),
                data: ERecord({
                  temp: ERecord({index: ENumber(0)}),
                  tag: EText('label'),
                  text: EBoolean(true),
                  texts: EList([
                    ERecord({
                      id: EText(ADD_TO_CART_ERRORS.QUANTITY.id),
                      name: EText(ADD_TO_CART_ERRORS.QUANTITY.name),
                      copy: EText(ADD_TO_CART_ERRORS.QUANTITY.copy),
                      category: EText(
                        ADD_TO_CART_ERRORS.QUANTITY.category.label
                      ),
                    }),
                    ERecord({
                      id: EText(ADD_TO_CART_ERRORS.GENERAL.id),
                      name: EText(ADD_TO_CART_ERRORS.GENERAL.name),
                      copy: EText(ADD_TO_CART_ERRORS.GENERAL.copy),
                      category: EText(
                        ADD_TO_CART_ERRORS.GENERAL.category.label
                      ),
                    }),
                    ERecord({
                      id: EText(ADD_TO_CART_ERRORS.MIXED_CART.id),
                      name: EText(ADD_TO_CART_ERRORS.MIXED_CART.name),
                      copy: EText(ADD_TO_CART_ERRORS.MIXED_CART.copy),
                      category: EText(
                        ADD_TO_CART_ERRORS.MIXED_CART.category.label
                      ),
                    }),
                    ERecord({
                      id: EText(ADD_TO_CART_ERRORS.BUY_NOW.id),
                      name: EText(ADD_TO_CART_ERRORS.BUY_NOW.name),
                      copy: EText(ADD_TO_CART_ERRORS.BUY_NOW.copy),
                      category: EText(
                        ADD_TO_CART_ERRORS.BUY_NOW.category.label
                      ),
                    }),
                    ERecord({
                      id: EText(ADD_TO_CART_ERRORS.CHECKOUT_DISABLED.id),
                      name: EText(ADD_TO_CART_ERRORS.CHECKOUT_DISABLED.name),
                      copy: EText(ADD_TO_CART_ERRORS.CHECKOUT_DISABLED.copy),
                      category: EText(
                        ADD_TO_CART_ERRORS.CHECKOUT_DISABLED.category.label
                      ),
                    }),
                    ERecord({
                      id: EText(ADD_TO_CART_ERRORS.SELECT_ALL_OPTIONS.id),
                      name: EText(ADD_TO_CART_ERRORS.SELECT_ALL_OPTIONS.name),
                      copy: EText(ADD_TO_CART_ERRORS.SELECT_ALL_OPTIONS.copy),
                      category: EText(
                        ADD_TO_CART_ERRORS.SELECT_ALL_OPTIONS.category.label
                      ),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildShippingMethodsListExpression = () =>
  EElement({
    id: 'Commerce Checkout Shipping Methods Wrapper',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutShippingMethodsWrapper')
    ),
    data: ERecord({
      tag: EText('form'),
      source: ELiteral({
        name: qualify(Plugins.name('Commerce'), typeName('LockedDynSource')),
        value: '',
      }),
      temp: ERecord({
        state: EEnum(SHIPPING_METHODS_STATES.DEFAULT),
      }),
      dyn: ERecord({
        condition: ERecord({
          class: ERecord({
            false: ERecord({
              'w-condition-invisible': ERecord({
                fields: ERecord({
                  requiresShipping: ERecord({
                    eq: EText('true'),
                  }),
                }),
              }),
            }),
          }),
        }),
      }),
      children: EList([
        EElement({
          id: 'Commerce Checkout Block Header',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutBlockHeader')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Heading',
                type: qualify(Plugins.name('Basic'), Component.name('Heading')),
                data: ERecord({
                  tag: EEnum('h2'),
                  children: EList([
                    EElement({
                      id: 'String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Shipping Method'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Checkout Shipping Method Block Wrapper',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutShippingMethodBlockWrapper')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Commerce Checkout Shipping Methods List',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutShippingMethodsList')
                ),
                data: ERecord({
                  tag: EEnum('div'),
                  children: EList([
                    EElement({
                      id: 'Commerce Checkout Shipping Method Item',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutShippingMethodItem')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Shipping Method Radio Button',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name(
                                'CommerceCheckoutShippingMethodRadioButton'
                              )
                            ),
                            data: ERecord({
                              attr: ERecord({
                                required: EBoolean(true),
                              }),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Shipping Method Description Block',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name(
                                'CommerceCheckoutShippingMethodDescriptionBlock'
                              )
                            ),
                            data: ERecord({
                              tag: EEnum('div'),
                              children: EList([
                                EElement({
                                  id: 'Commerce Bold Text Block',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceBoldTextBlock')
                                  ),
                                  data: ERecord({
                                    text: EBoolean(true),
                                    tag: EEnum('div'),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('name'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('description'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Block 2',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('Block')
                            ),
                            data: ERecord({
                              tag: EEnum('div'),
                              text: EBoolean(true),
                              dyn: ERecord({
                                bind: ERecord({
                                  innerHTML: EList([
                                    ERecord({
                                      slug: EText('price'),
                                      type: EText('CommercePrice'),
                                    }),
                                  ]),
                                }),
                              }),
                              children: EList([]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Checkout Shipping Methods Empty State',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutShippingMethodsEmptyState')
                ),
                data: ERecord({
                  tag: EEnum('div'),
                  children: EList([
                    EElement({
                      id: 'Block 3',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('Block')
                      ),
                      data: ERecord({
                        tag: EEnum('div'),
                        text: EBoolean(true),
                        children: EList([
                          EElement({
                            id: 'String 2',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('String')
                            ),
                            data: EText(
                              'No shipping methods are available for the address given.'
                            ),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildCheckoutForm = ({
  billingAddressToggleId,
  collectionState,
  betaFeatures,
}: {
  billingAddressToggleId: string;
  collectionState: CollectionState;
  betaFeatures: BetaFeatureFlags | null;
}): Expression => {
  const emailID = 'wf-ecom-email';

  const shippingFullNameID = 'wf-ecom-shipping-name';
  const shippingAddressID = 'wf-ecom-shipping-address';
  const shippingCityID = 'wf-ecom-shipping-city';
  const shippingStateID = 'wf-ecom-shipping-state';
  const shippingZipID = 'wf-ecom-shipping-zip';
  const shippingCountryID = 'wf-ecom-shipping-country';

  const billingFullNameID = 'wf-ecom-billing-name';
  const billingAddressID = 'wf-ecom-billing-address';
  const billingCityID = 'wf-ecom-billing-city';
  const billingStateID = 'wf-ecom-billing-state';
  const billingZipID = 'wf-ecom-billing-zip';
  const billingCountryID = 'wf-ecom-billing-country';

  return EElement({
    id: 'Commerce Checkout Form Container',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutFormContainer')
    ),
    data: ERecord({
      commerce: ERecord({
        type: EText('checkout-form-container'),
      }),
      temp: ERecord({
        requiresShipping: EEnum(ORDER_TYPE.REQUIRE_SHIPPING),
        state: EEnum(CHECKOUT_STATES.DEFAULT),
        previewItems: ENumber(PREVIEW_ITEMS_DEFAULT),
        note: ELiteral({
          name: qualify(Plugins.name('Commerce'), typeName('DismissibleNote')),
          value: false,
        }),
        [BILLING_ADDRESS_TOGGLE_KEY]: EBoolean(false),
      }),
      children: EList([
        EElement({
          id: 'Commerce Layout Container',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceLayoutContainer')
          ),
          data: ERecord({
            tag: EEnum('div'),
            children: EList([
              EElement({
                id: 'Commerce Layout Main',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutMain')
                ),
                data: ERecord({
                  children: EList([
                    buildQuickCheckoutActionsExpression(),
                    EElement({
                      id: 'Commerce Checkout Customer Info Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutCustomerInfoWrapper')
                      ),
                      data: ERecord({
                        tag: EText('section'),
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Block Header',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockHeader')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    styleBlockIds: EList([
                                      EText(
                                        '5a6ba33f-da38-2c10-c079-a55f6095fb43'
                                      ),
                                    ]),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 2',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Customer Info'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block 2',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 3',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('* Required'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Label',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(emailID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 4',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Email *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Email Input',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutEmailInput')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(emailID),
                                    }),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Shipping Address Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutShippingAddressWrapper')
                      ),
                      data: ERecord({
                        tag: EText('section'),
                        dyn: ERecord({
                          condition: ERecord({
                            class: ERecord({
                              false: ERecord({
                                'w-condition-invisible': ERecord({
                                  fields: ERecord({
                                    requiresShipping: ERecord({
                                      eq: EText('true'),
                                    }),
                                  }),
                                }),
                              }),
                            }),
                          }),
                        }),
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Block Header 2',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockHeader')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading 2',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 5',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Shipping Address'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block 3',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 6',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('* Required'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content 2',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Label 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(shippingFullNameID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 7',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Full Name *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Shipping Full Name',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutShippingFullName'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(shippingFullNameID),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Label 3',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(shippingAddressID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 8',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Street Address *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Shipping Street Address',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutShippingStreetAddress'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(shippingAddressID),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Shipping Street Address Optional',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutShippingStreetAddressOptional'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      'aria-label': EText(
                                        'Shipping address optional'
                                      ),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Row',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutRow')
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Column',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 4',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(shippingCityID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 9',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText('City *'),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Shipping City',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutShippingCity'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(shippingCityID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 2',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 5',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(shippingStateID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 10',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'State/Province'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Shipping State Province',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutShippingStateProvince'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(shippingStateID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 3',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 6',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(shippingZipID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 11',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'Zip/Postal Code *'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Shipping Zip Postal Code',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutShippingZipPostalCode'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(shippingZipID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Label 7',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(shippingCountryID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 12',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Country *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Shipping Country Selector',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutShippingCountrySelector'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(shippingCountryID),
                                    }),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    buildShippingMethodsListExpression(),
                    EElement({
                      id: 'Commerce Checkout Payment Info Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutPaymentInfoWrapper')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Block Header 4',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockHeader')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading 4',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 15',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Payment Info'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block 7',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 16',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('* Required'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content 3',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Label 8',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 17',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Card Number *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Card Number',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutCardNumber')
                                  ),
                                  data: ERecord({}),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Row 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutRow')
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Column 4',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 9',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 18',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'Expiration Date *'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Card Expiration Date',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutCardExpirationDate'
                                                )
                                              ),
                                              data: ERecord({}),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 5',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 10',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 19',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'Security Code *'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Card Security Code',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutCardSecurityCode'
                                                )
                                              ),
                                              data: ERecord({}),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Address Toggle Wrapper',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingAddressToggleWrapper'
                                    )
                                  ),
                                  data: ERecord({
                                    dyn: ERecord({
                                      condition: ERecord({
                                        class: ERecord({
                                          false: ERecord({
                                            'w-condition-invisible': ERecord({
                                              fields: ERecord({
                                                requiresShipping: ERecord({
                                                  eq: EText('true'),
                                                }),
                                              }),
                                            }),
                                          }),
                                        }),
                                      }),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Billing Address Toggle Checkbox',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutBillingAddressToggleCheckbox'
                                          )
                                        ),
                                        data: ERecord({
                                          attr: ERecord({
                                            id: EText(billingAddressToggleId),
                                          }),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Billing Address Toggle Label',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutBillingAddressToggleLabel'
                                          )
                                        ),
                                        data: ERecord({
                                          attr: ERecord({
                                            for: EText(billingAddressToggleId),
                                          }),
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Form Container String 20',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('String')
                                              ),
                                              data: EText(
                                                'Billing address same as shipping'
                                              ),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Billing Address Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutBillingAddressWrapper')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Block Header 5',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockHeader')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading 5',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 21',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Billing Address'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block 8',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 22',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('* Required'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content 4',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Label 11',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(billingFullNameID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 23',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Full Name *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Full Name',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingFullName'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(billingFullNameID),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Label 12',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(billingAddressID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 24',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Street Address *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Street Address',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingStreetAddress'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(billingAddressID),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Street Address Optional',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingStreetAddressOptional'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      'aria-label': EText(
                                        'Billing address optional'
                                      ),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Row 3',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutRow')
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Column 6',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 13',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(billingCityID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 25',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText('City *'),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Billing City',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutBillingCity'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(billingCityID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 7',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 14',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(billingStateID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 26',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'State/Province'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Billing State Province',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutBillingStateProvince'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(billingStateID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 8',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 15',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(billingZipID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 27',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'Zip/Postal Code *'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Billing Zip Postal Code',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutBillingZipPostalCode'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(billingZipID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Label 16',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(billingCountryID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 28',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Country *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Country Selector',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingCountrySelector'
                                    )
                                  ),
                                  data: ERecord({
                                    defaultCountry: EEnum('US'),
                                    attr: ERecord({
                                      id: EText(billingCountryID),
                                    }),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    buildCheckoutItemsSummaryExpression({
                      bindings:
                        buildOrderItemsBindingsFromCollectionState(
                          collectionState
                        ),
                      betaFeatures,
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Layout Sidebar',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutSidebar')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Commerce Checkout Order Summary Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutOrderSummaryWrapper')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Summary Block Header 2',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name(
                                'CommerceCheckoutSummaryBlockHeader'
                              )
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading 7',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 32',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Order Summary'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content 6',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Summary Line Item',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryLineItem'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container Block 13',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Form Container String 33',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('String')
                                              ),
                                              data: EText('Subtotal'),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Form Container Block 14',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText('subtotal'),
                                                  type: EText('CommercePrice'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Order Summary Extra Items List',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutOrderSummaryExtraItemsList'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Order Summary Extra Items List Item',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutOrderSummaryExtraItemsListItem'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Form Container Block 15',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('Block')
                                              ),
                                              data: ERecord({
                                                tag: EEnum('div'),
                                                text: EBoolean(true),
                                                dyn: ERecord({
                                                  bind: ERecord({
                                                    innerHTML: EList([
                                                      ERecord({
                                                        slug: EText('name'),
                                                        type: EText(
                                                          'PlainText'
                                                        ),
                                                      }),
                                                    ]),
                                                  }),
                                                }),
                                                children: EList([]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Form Container Block 16',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('Block')
                                              ),
                                              data: ERecord({
                                                tag: EEnum('div'),
                                                text: EBoolean(true),
                                                dyn: ERecord({
                                                  bind: ERecord({
                                                    innerHTML: EList([
                                                      ERecord({
                                                        slug: EText('price'),
                                                        type: EText(
                                                          'CommercePrice'
                                                        ),
                                                      }),
                                                    ]),
                                                  }),
                                                }),
                                                children: EList([]),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Summary Line Item 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryLineItem'
                                    )
                                  ),
                                  data: ERecord({
                                    styleBlockIds: EList([
                                      EText(
                                        'e3eb4767-2a16-c07f-c89a-0ae850db67bc'
                                      ),
                                    ]),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container Block 17',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Form Container String 34',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('String')
                                              ),
                                              data: EText('Total'),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Total',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTotal'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText('total'),
                                                  type: EText('CommercePrice'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Place Order Button',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutPlaceOrderButton')
                      ),
                      data: ERecord({
                        attr: ERecord({
                          href: EText('#'),
                          value: EText('Place Order'),
                        }),
                        commerce: ERecord({
                          [LOADING_TEXT]: EText(
                            CHECKOUT_PLACE_ORDER_LOADING_TEXT_DEFAULT
                          ),
                          type: EText('place-order-button'),
                        }),
                        styleBlockIds: EList([
                          EText('b18b236b-e42e-31c2-5435-f5d36aa2b5ac'),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Error State',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutErrorState')
                      ),
                      data: ERecord({
                        commerce: ERecord({
                          type: EText('checkout-error-state'),
                        }),
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Error Msg',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutErrorMsg')
                            ),
                            data: ERecord({
                              temp: ERecord({index: ENumber(0)}),
                              tag: EText('label'),
                              text: EBoolean(true),
                              commerce: ERecord({
                                type: EText('.w-add-to-cart-error-msg'),
                              }),
                              texts: EList([
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.INFO.id),
                                  name: EText(CHECKOUT_ERRORS.INFO.name),
                                  copy: EText(CHECKOUT_ERRORS.INFO.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.INFO.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.SHIPPING.id),
                                  name: EText(CHECKOUT_ERRORS.SHIPPING.name),
                                  copy: EText(CHECKOUT_ERRORS.SHIPPING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.SHIPPING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.EXTRAS.id),
                                  name: EText(CHECKOUT_ERRORS.EXTRAS.name),
                                  copy: EText(CHECKOUT_ERRORS.EXTRAS.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.EXTRAS.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PRICING.id),
                                  name: EText(CHECKOUT_ERRORS.PRICING.name),
                                  copy: EText(CHECKOUT_ERRORS.PRICING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PRICING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PRODUCT.id),
                                  name: EText(CHECKOUT_ERRORS.PRODUCT.name),
                                  copy: EText(CHECKOUT_ERRORS.PRODUCT.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PRODUCT.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PAYMENT.id),
                                  name: EText(CHECKOUT_ERRORS.PAYMENT.name),
                                  copy: EText(CHECKOUT_ERRORS.PAYMENT.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PAYMENT.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.BILLING.id),
                                  name: EText(CHECKOUT_ERRORS.BILLING.name),
                                  copy: EText(CHECKOUT_ERRORS.BILLING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.BILLING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.MINIMUM.id),
                                  name: EText(CHECKOUT_ERRORS.MINIMUM.name),
                                  copy: EText(CHECKOUT_ERRORS.MINIMUM.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.MINIMUM.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.category
                                      .label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.category
                                      .label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT
                                      .category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.category
                                      .label
                                  ),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });
};

export const buildCheckoutFormBackendExpression = ({
  billingAddressToggleId,
  backendProductCollection,
  backendSkuCollection,
  betaFeatures,
}: {
  billingAddressToggleId: string;
  backendProductCollection: BackendCollection;
  backendSkuCollection: BackendCollection;
  betaFeatures: BetaFeatureFlags | null;
}) => {
  const emailID = 'wf-ecom-email';

  const shippingFullNameID = 'wf-ecom-shipping-name';
  const shippingAddressID = 'wf-ecom-shipping-address';
  const shippingCityID = 'wf-ecom-shipping-city';
  const shippingStateID = 'wf-ecom-shipping-state';
  const shippingZipID = 'wf-ecom-shipping-zip';
  const shippingCountryID = 'wf-ecom-shipping-country';

  const billingFullNameID = 'wf-ecom-billing-name';
  const billingAddressID = 'wf-ecom-billing-address';
  const billingCityID = 'wf-ecom-billing-city';
  const billingStateID = 'wf-ecom-billing-state';
  const billingZipID = 'wf-ecom-billing-zip';
  const billingCountryID = 'wf-ecom-billing-country';

  return EElement({
    id: 'Commerce Checkout Form Container',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutFormContainer')
    ),
    data: ERecord({
      commerce: ERecord({
        type: EText('checkout-form-container'),
      }),
      temp: ERecord({
        requiresShipping: EEnum('shipping'),
        state: EEnum('DEFAULT'),
        previewItems: ENumber(3),
        note: ELiteral({
          name: qualify(Plugins.name('Commerce'), typeName('DismissibleNote')),
          value: false,
        }),
      }),
      children: EList([
        EElement({
          id: 'Commerce Layout Container',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceLayoutContainer')
          ),
          data: ERecord({
            tag: EEnum('div'),
            children: EList([
              EElement({
                id: 'Commerce Layout Main',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutMain')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Commerce Cart Quick Checkout Actions',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCartQuickCheckoutActions')
                      ),
                      data: ERecord({
                        commerce: ERecord({
                          type: EText('cart-quick-checkout-actions'),
                          defaultApplePay: EBoolean(true),
                        }),
                        children: EList([
                          EElement({
                            id: 'Commerce Cart Apple Pay Button',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCartApplePayButton')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Cart Apple Pay Icon',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCartApplePayIcon')
                                  ),
                                  data: ERecord({}),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Cart Quick Checkout Button',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCartQuickCheckoutButton')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Quick Checkout Google Icon',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceQuickCheckoutGoogleIcon'
                                    )
                                  ),
                                  data: ERecord({}),
                                }),
                                EElement({
                                  id: 'Commerce Quick Checkout Microsoft Icon',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceQuickCheckoutMicrosoftIcon'
                                    )
                                  ),
                                  data: ERecord({}),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Pay with browser.'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Customer Info Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutCustomerInfoWrapper')
                      ),
                      data: ERecord({
                        tag: EText('section'),
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Block Header',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockHeader')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 2',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Customer Info'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block 2',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 3',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('* Required'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Label',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(emailID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 4',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Email *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Email Input',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutEmailInput')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(emailID),
                                    }),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Shipping Address Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutShippingAddressWrapper')
                      ),
                      data: ERecord({
                        tag: EText('section'),
                        dyn: ERecord({
                          condition: ERecord({
                            class: ERecord({
                              false: ERecord({
                                'w-condition-invisible': ERecord({
                                  fields: ERecord({
                                    requiresShipping: ERecord({
                                      eq: EText('true'),
                                    }),
                                  }),
                                }),
                              }),
                            }),
                          }),
                        }),
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Block Header 2',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockHeader')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading 2',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 5',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Shipping Address'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block 3',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 6',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('* Required'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content 2',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Label 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(shippingFullNameID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 7',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Full Name *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Shipping Full Name',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutShippingFullName'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(shippingFullNameID),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Label 3',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(shippingAddressID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 8',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Street Address *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Shipping Street Address',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutShippingStreetAddress'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(shippingAddressID),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Shipping Street Address Optional',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutShippingStreetAddressOptional'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      'aria-label': EText(
                                        'Shipping address optional'
                                      ),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Row',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutRow')
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Column',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 4',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(shippingCityID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 9',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText('City *'),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Shipping City',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutShippingCity'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(shippingCityID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 2',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 5',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(shippingStateID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 10',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'State/Province'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Shipping State Province',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutShippingStateProvince'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(shippingStateID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 3',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 6',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(shippingZipID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 11',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'Zip/Postal Code *'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Shipping Zip Postal Code',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutShippingZipPostalCode'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(shippingZipID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Label 7',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(shippingCountryID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 12',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Country *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Shipping Country Selector',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutShippingCountrySelector'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(shippingCountryID),
                                    }),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    buildShippingMethodsListExpression(),
                    EElement({
                      id: 'Commerce Checkout Payment Info Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutPaymentInfoWrapper')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Block Header 4',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockHeader')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading 4',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 15',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Payment Info'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block 7',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 16',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('* Required'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content 3',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Label 8',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 17',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Card Number *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Card Number',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutCardNumber')
                                  ),
                                  data: ERecord({}),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Row 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutRow')
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Column 4',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 9',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 18',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'Expiration Date *'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Card Expiration Date',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutCardExpirationDate'
                                                )
                                              ),
                                              data: ERecord({}),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 5',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 10',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 19',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'Security Code *'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Card Security Code',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutCardSecurityCode'
                                                )
                                              ),
                                              data: ERecord({}),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Address Toggle Wrapper',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingAddressToggleWrapper'
                                    )
                                  ),
                                  data: ERecord({
                                    dyn: ERecord({
                                      condition: ERecord({
                                        class: ERecord({
                                          false: ERecord({
                                            'w-condition-invisible': ERecord({
                                              fields: ERecord({
                                                requiresShipping: ERecord({
                                                  eq: EText('true'),
                                                }),
                                              }),
                                            }),
                                          }),
                                        }),
                                      }),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Billing Address Toggle Checkbox',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutBillingAddressToggleCheckbox'
                                          )
                                        ),
                                        data: ERecord({
                                          attr: ERecord({
                                            id: EText(billingAddressToggleId),
                                          }),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Billing Address Toggle Label',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutBillingAddressToggleLabel'
                                          )
                                        ),
                                        data: ERecord({
                                          attr: ERecord({
                                            for: EText(billingAddressToggleId),
                                          }),
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Form Container String 20',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('String')
                                              ),
                                              data: EText(
                                                'Billing address same as shipping'
                                              ),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Billing Address Wrapper',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutBillingAddressWrapper')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Block Header 5',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockHeader')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Form Container Heading 5',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Heading')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('h2'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 21',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Billing Address'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Form Container Block 8',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 22',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('* Required'),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Block Content 4',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutBlockContent')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Label 11',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(billingFullNameID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 23',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Full Name *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Full Name',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingFullName'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(billingFullNameID),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Label 12',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(billingAddressID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 24',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Street Address *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Street Address',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingStreetAddress'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(billingAddressID),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Street Address Optional',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingStreetAddressOptional'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      'aria-label': EText(
                                        'Billing address optional'
                                      ),
                                    }),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Row 3',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutRow')
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Column 6',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 13',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(billingCityID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 25',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText('City *'),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Billing City',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutBillingCity'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(billingCityID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 7',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 14',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(billingStateID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 26',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'State/Province'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Billing State Province',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutBillingStateProvince'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(billingStateID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Column 8',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutColumn'
                                          )
                                        ),
                                        data: ERecord({
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Label 15',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  for: EText(billingZipID),
                                                }),
                                                children: EList([
                                                  EElement({
                                                    id: 'Commerce Checkout Form Container String 27',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(
                                                      'Zip/Postal Code *'
                                                    ),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Billing Zip Postal Code',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutBillingZipPostalCode'
                                                )
                                              ),
                                              data: ERecord({
                                                attr: ERecord({
                                                  id: EText(billingZipID),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Label 16',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceCheckoutLabel')
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      for: EText(billingCountryID),
                                    }),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Form Container String 28',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Country *'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Billing Country Selector',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutBillingCountrySelector'
                                    )
                                  ),
                                  data: ERecord({
                                    attr: ERecord({
                                      id: EText(billingCountryID),
                                    }),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    buildCheckoutItemsSummaryExpression({
                      bindings:
                        buildCheckoutItemsBindingsFromBackendCollections(
                          backendProductCollection,
                          backendSkuCollection
                        ),
                      betaFeatures,
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Layout Sidebar',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutSidebar')
                ),
                data: ERecord({
                  children: EList([
                    buildCheckoutOrderSummaryExpression(),
                    EElement({
                      id: 'Commerce Checkout Place Order Button',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutPlaceOrderButton')
                      ),
                      data: ERecord({
                        attr: ERecord({
                          href: EText('#'),
                          value: EText('Place Order'),
                        }),
                        commerce: ERecord({
                          [LOADING_TEXT]: EText(
                            CHECKOUT_PLACE_ORDER_LOADING_TEXT_DEFAULT
                          ),
                          type: EText('place-order-button'),
                        }),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Error State',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutErrorState')
                      ),
                      data: ERecord({
                        commerce: ERecord({
                          type: EText('checkout-error-state'),
                        }),
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Error Msg',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutErrorMsg')
                            ),
                            data: ERecord({
                              temp: ERecord({index: ENumber(0)}),
                              tag: EText('label'),
                              text: EBoolean(true),
                              commerce: ERecord({
                                type: EText('.w-add-to-cart-error-msg'),
                              }),
                              texts: EList([
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.INFO.id),
                                  name: EText(CHECKOUT_ERRORS.INFO.name),
                                  copy: EText(CHECKOUT_ERRORS.INFO.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.INFO.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.SHIPPING.id),
                                  name: EText(CHECKOUT_ERRORS.SHIPPING.name),
                                  copy: EText(CHECKOUT_ERRORS.SHIPPING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.SHIPPING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.EXTRAS.id),
                                  name: EText(CHECKOUT_ERRORS.EXTRAS.name),
                                  copy: EText(CHECKOUT_ERRORS.EXTRAS.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.EXTRAS.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PRICING.id),
                                  name: EText(CHECKOUT_ERRORS.PRICING.name),
                                  copy: EText(CHECKOUT_ERRORS.PRICING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PRICING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PRODUCT.id),
                                  name: EText(CHECKOUT_ERRORS.PRODUCT.name),
                                  copy: EText(CHECKOUT_ERRORS.PRODUCT.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PRODUCT.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PAYMENT.id),
                                  name: EText(CHECKOUT_ERRORS.PAYMENT.name),
                                  copy: EText(CHECKOUT_ERRORS.PAYMENT.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PAYMENT.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.BILLING.id),
                                  name: EText(CHECKOUT_ERRORS.BILLING.name),
                                  copy: EText(CHECKOUT_ERRORS.BILLING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.BILLING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.MINIMUM.id),
                                  name: EText(CHECKOUT_ERRORS.MINIMUM.name),
                                  copy: EText(CHECKOUT_ERRORS.MINIMUM.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.MINIMUM.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.category
                                      .label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.category
                                      .label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT
                                      .category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.category
                                      .label
                                  ),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });
};

export const buildOrderConfirmation = ({
  collectionState,
  betaFeatures,
}: {
  collectionState: CollectionState;
  betaFeatures: BetaFeatureFlags | null;
}) =>
  EElement({
    id: 'Commerce Order Confirmation Container',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceOrderConfirmationContainer')
    ),
    data: ERecord({
      commerce: ERecord({
        type: EText('order-confirmation-container'),
      }),
      temp: ERecord({
        requiresShipping: EEnum(ORDER_TYPE.REQUIRE_SHIPPING),
        previewItems: ENumber(PREVIEW_ITEMS_DEFAULT),
        note: ELiteral({
          name: qualify(Plugins.name('Commerce'), typeName('DismissibleNote')),
          value: false,
        }),
      }),
      children: EList([
        EElement({
          id: 'Commerce Layout Container',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceLayoutContainer')
          ),
          data: ERecord({
            tag: EEnum('div'),
            children: EList([
              EElement({
                id: 'Commerce Layout Main',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutMain')
                ),
                data: ERecord({
                  children: EList([
                    buildCheckoutCustomerInfoSummaryExpression(),
                    buildCheckoutShippingSummaryExpression(),
                    buildCheckoutPaymentSummaryExpression(),
                    buildCheckoutItemsSummaryExpression({
                      bindings:
                        buildOrderItemsBindingsFromCollectionState(
                          collectionState
                        ),
                      betaFeatures,
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Layout Sidebar',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutSidebar')
                ),
                data: ERecord({
                  children: EList([buildCheckoutOrderSummaryExpression()]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildOrderConfirmationExpressionBackend = ({
  productCollection,
  skuCollection,
  betaFeatures,
}: {
  productCollection: BackendCollection;
  skuCollection: BackendCollection;
  betaFeatures: BetaFeatureFlags | null;
}) =>
  EElement({
    id: 'Commerce Order Confirmation Container',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceOrderConfirmationContainer')
    ),
    data: ERecord({
      commerce: ERecord({
        type: EText('order-confirmation-container'),
      }),
      temp: ERecord({
        requiresShipping: EEnum(ORDER_TYPE.REQUIRE_SHIPPING),
        state: EEnum(CHECKOUT_STATES.DEFAULT),
        previewItems: ENumber(PREVIEW_ITEMS_DEFAULT),
        note: ELiteral({
          name: qualify(Plugins.name('Commerce'), typeName('DismissibleNote')),
          value: false,
        }),
      }),
      children: EList([
        EElement({
          id: 'Commerce Layout Container',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceLayoutContainer')
          ),
          data: ERecord({
            tag: EEnum('div'),
            children: EList([
              EElement({
                id: 'Commerce Layout Main',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutMain')
                ),
                data: ERecord({
                  children: EList([
                    buildCheckoutCustomerInfoSummaryExpression(),
                    buildCheckoutShippingSummaryExpression(),
                    buildCheckoutPaymentSummaryExpression(),
                    buildCheckoutItemsSummaryExpression({
                      bindings:
                        buildCheckoutItemsBindingsFromBackendCollections(
                          productCollection,
                          skuCollection
                        ),
                      betaFeatures,
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Layout Sidebar',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutSidebar')
                ),
                data: ERecord({
                  children: EList([buildCheckoutOrderSummaryExpression()]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildCheckoutCustomerInfoSummaryExpression = () =>
  EElement({
    id: 'Commerce Checkout Customer Info Summary Wrapper',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutCustomerInfoSummaryWrapper')
    ),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Commerce Checkout Summary Block Header',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutSummaryBlockHeader')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Commerce Order Confirmation Container Heading',
                type: qualify(Plugins.name('Basic'), Component.name('Heading')),
                data: ERecord({
                  tag: EEnum('h2'),
                  children: EList([
                    EElement({
                      id: 'Commerce Order Confirmation Container String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Customer Information'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Checkout Block Content',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutBlockContent')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Commerce Checkout Row',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutRow')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Commerce Checkout Column',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutColumn')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Summary Item',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutSummaryItem')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Summary Label',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryLabel'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Order Confirmation Container String 2',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Email'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Order Confirmation Container Block',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('customerEmail'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Column 2',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutColumn')
                      ),
                      data: ERecord({
                        dyn: ERecord({
                          condition: ERecord({
                            class: ERecord({
                              false: ERecord({
                                'w-condition-invisible': ERecord({
                                  fields: ERecord({
                                    requiresShipping: ERecord({
                                      eq: EText('true'),
                                    }),
                                  }),
                                }),
                              }),
                            }),
                          }),
                        }),
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Summary Item 2',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutSummaryItem')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Summary Label 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryLabel'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Order Confirmation Container String 3',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Shipping Address'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Order Confirmation Container Block 2',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText(
                                              'shippingAddressAddressee'
                                            ),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Order Confirmation Container Block 3',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('shippingAddressLine1'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Order Confirmation Container Block 4',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('shippingAddressLine2'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Summary Flex Box Div',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryFlexBoxDiv'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'shippingAddressCity'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 2',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'shippingAddressState'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 3',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'shippingAddressPostalCode'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Order Confirmation Container Block 5',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText(
                                              'shippingAddressCountry'
                                            ),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildOrderItemsBindingsFromCollectionState = (
  collectionState: CollectionState
) => {
  const {collections} = collectionState;

  const userItemsSkuField = getCartItemsSkuField(collections);
  const userItemsProductField = getCartItemsProductField(collections);

  // get the price field in sku and its value
  const userItemsSkuPriceField = getSkuPriceField(collections);
  // get the name field in product and its value
  const userItemsProductNameField = getProductNameField(collections);
  // get the url field in sku and its value
  const userItemsSkuImageField = getSkuImageField(collections);

  if (
    userItemsSkuField == null ||
    userItemsProductField == null ||
    userItemsSkuPriceField == null ||
    userItemsProductNameField == null ||
    userItemsSkuImageField == null
  ) {
    throw new Error(
      'Missing fields data that is required to create a new Cart'
    );
  }

  return {
    userItemsSkuFieldBinding: toBindingObject(userItemsSkuField),
    userItemsProductFieldBinding: toBindingObject(userItemsProductField),
    userItemsProductNameFieldBinding: toBindingObject(
      userItemsProductNameField
    ),
    userItemsSkuImageFieldBinding: toBindingObject(userItemsSkuImageField),
    userItemsSkuPriceFieldBinding: toBindingObject(userItemsSkuPriceField),
  };
};

const getBackendFieldFromCollection = (
  collection: Collection,
  fieldSlug: string
) => collection.fields.find((field) => field.slug === fieldSlug);

const buildCheckoutItemsBindingsFromBackendCollections = (
  productCollection: BackendCollection,
  skuCollection: BackendCollection
) => {
  const userItemsSkuField = buildSkuItemRefField(productCollection._id);
  const userItemsProductField = buildProductItemRefField(skuCollection._id);

  // get the price field in sku and its value
  const userItemsSkuPriceField = getBackendFieldFromCollection(
    skuCollection,
    'price'
  );
  // get the name field in product and its value
  const userItemsProductNameField = getBackendFieldFromCollection(
    productCollection,
    'name'
  );
  // get the url field in sku and its value
  const userItemsSkuImageField = getBackendFieldFromCollection(
    skuCollection,
    'main-image'
  );

  if (
    userItemsSkuField == null ||
    userItemsProductField == null ||
    userItemsSkuPriceField == null ||
    userItemsProductNameField == null ||
    userItemsSkuImageField == null
  ) {
    return null;
  }
  return {
    userItemsSkuFieldBinding: toBindingObject(userItemsSkuField),
    userItemsProductFieldBinding: toBindingObject(userItemsProductField),
    userItemsProductNameFieldBinding: toBindingObject(
      userItemsProductNameField
    ),
    userItemsSkuImageFieldBinding: toBindingObject(userItemsSkuImageField),
  };
};

type CheckoutItemsBindingsData = {
  userItemsSkuFieldBinding: BindingJson | null;
  userItemsProductFieldBinding: BindingJson | null;
  userItemsProductNameFieldBinding: BindingJson | null;
  userItemsSkuImageFieldBinding: BindingJson | null;
};

export const buildCheckoutItemsSummaryExpression = ({
  bindings,
  betaFeatures,
}: {
  bindings: CheckoutItemsBindingsData | null;
  betaFeatures: BetaFeatureFlags | null;
}) => {
  const isTrialEnabled = betaFeatures?.trialSubscription;

  const {
    userItemsSkuFieldBinding,
    userItemsProductFieldBinding,
    userItemsProductNameFieldBinding,
    userItemsSkuImageFieldBinding,
  } = _getCompleteCheckoutItemsBindingsDataOrThrow(bindings);

  return EElement({
    id: 'Commerce Checkout Order Items Wrapper',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutOrderItemsWrapper')
    ),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Commerce Checkout Summary Block Header',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutSummaryBlockHeader')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Heading',
                type: qualify(Plugins.name('Basic'), Component.name('Heading')),
                data: ERecord({
                  tag: EEnum('h2'),
                  children: EList([
                    EElement({
                      id: 'String 1',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Items in Order'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Checkout Block Content',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutBlockContent')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Commerce Checkout Order Items List',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutOrderItemsList')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Commerce Checkout Order Item',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutOrderItem')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Cart Item Image',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCartItemImage')
                            ),
                            data: ERecord({
                              dyn: ERecord({
                                bind: ERecord({
                                  src: EList(
                                    [
                                      userItemsSkuFieldBinding,
                                      userItemsSkuImageFieldBinding,
                                    ].map(_bindingsDataToERecord)
                                  ),
                                }),
                              }),
                              children: EList([]),
                            }),
                          }),
                          EElement({
                            id: 'Commerce Checkout Order Item Description Wrapper',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name(
                                'CommerceCheckoutOrderItemDescriptionWrapper'
                              )
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Bold Text Block',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name('CommerceBoldTextBlock')
                                  ),
                                  data: ERecord({
                                    text: EBoolean(true),
                                    tag: EEnum('div'),
                                    commerce: ERecord({
                                      type: EText('cart-product-name'),
                                    }),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList(
                                          [
                                            userItemsProductFieldBinding,
                                            userItemsProductNameFieldBinding,
                                          ].map(_bindingsDataToERecord)
                                        ),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                ...(isTrialEnabled
                                  ? [buildCheckoutItemTrialTextWrapper()]
                                  : []),
                                EElement({
                                  id: 'Commerce Checkout Order Item Quantity Wrapper',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutOrderItemQuantityWrapper'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    children: EList([
                                      EElement({
                                        id: 'Block 1',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          children: EList([
                                            EElement({
                                              id: 'String 2',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('String')
                                              ),
                                              data: EText('Quantity: '),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Block 2',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText('count'),
                                                  type: EText('Number'),
                                                  filter: EText(
                                                    'numberPrecision|0|numberPrecision'
                                                  ),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Order Item Option List',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutOrderItemOptionList'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('ul'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Order Item Option List Item',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutOrderItemOptionListItem'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('li'),
                                          children: EList([
                                            EElement({
                                              id: 'Commerce Checkout Order Item Option List Item Label',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutOrderItemOptionListItemLabel'
                                                )
                                              ),
                                              data: ERecord({
                                                text: EBoolean(true),
                                                dyn: ERecord({
                                                  bind: ERecord({
                                                    innerHTML: EList([
                                                      ERecord({
                                                        id: EText(''),
                                                        slug: EText('name'),
                                                        type: EText(
                                                          'PlainText'
                                                        ),
                                                      }),
                                                    ]),
                                                  }),
                                                }),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Span 3',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('Span')
                                              ),
                                              data: ERecord({
                                                text: EBoolean(true),
                                                children: EList([
                                                  EElement({
                                                    id: 'String 3',
                                                    type: qualify(
                                                      Plugins.name('Basic'),
                                                      Component.name('String')
                                                    ),
                                                    data: EText(': '),
                                                  }),
                                                ]),
                                              }),
                                            }),
                                            EElement({
                                              id: 'Commerce Checkout Order Item Option List Item Value',
                                              type: qualify(
                                                Plugins.name('Commerce'),
                                                Component.name(
                                                  'CommerceCheckoutOrderItemOptionListItemValue'
                                                )
                                              ),
                                              data: ERecord({
                                                text: EBoolean(true),
                                                dyn: ERecord({
                                                  bind: ERecord({
                                                    innerHTML: EList([
                                                      ERecord({
                                                        id: EText(''),
                                                        slug: EText('value'),
                                                        type: EText(
                                                          'PlainText'
                                                        ),
                                                      }),
                                                    ]),
                                                  }),
                                                }),
                                              }),
                                            }),
                                          ]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Block 4',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('Block')
                            ),
                            data: ERecord({
                              tag: EEnum('div'),
                              text: EBoolean(true),
                              dyn: ERecord({
                                bind: ERecord({
                                  innerHTML: EList([
                                    ERecord({
                                      slug: EText('rowTotal'),
                                      type: EText('CommercePrice'),
                                    }),
                                  ]),
                                }),
                              }),
                              children: EList([]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });
};

export const buildCheckoutItemTrialTextWrapper = () => {
  return EElement({
    id: 'Commerce Checkout Order Item Subscription Trial Text Block',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutOrderItemTrialTextWrapper')
    ),
    data: ERecord({
      text: EBoolean(true),
      tag: EEnum('div'),
      dyn: ERecord({
        condition: ERecord({
          class: ERecord({
            false: ERecord({
              'w-condition-invisible': ERecord({
                fields: ERecord({
                  subscriptionTrial: ERecord({
                    exists: EText('yes'),
                  }),
                }),
              }),
            }),
          }),
        }),
      }),
      children: EList([
        EElement({
          id: 'Span 1',
          type: qualify(Plugins.name('Basic'), Component.name('Span')),
          data: ERecord({
            children: EList([
              EElement({
                id: 'String 1',
                type: qualify(Plugins.name('Basic'), Component.name('String')),
                data: EText('Try it free for '),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Span 2',
          type: qualify(Plugins.name('Basic'), Component.name('Span')),
          data: ERecord({
            dyn: ERecord({
              bind: ERecord({
                innerHTML: EList([
                  ERecord({
                    slug: EText('subscriptionTrial'),
                    type: EText('Number'),
                    filter: EText('numberPrecision|0|numberPrecision'),
                  }),
                ]),
              }),
            }),
            children: EList([]),
          }),
        }),
        EElement({
          id: 'Span 3',
          type: qualify(Plugins.name('Basic'), Component.name('Block')),
          data: ERecord({
            tag: EEnum('div'),
            text: EBoolean(true),
            children: EList([
              EElement({
                id: 'String 1',
                type: qualify(Plugins.name('Basic'), Component.name('String')),
                data: EText(
                  ' days. After trial ends, billing begins at the regular price.'
                ),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });
};

function _bindingsDataToERecord(binding: BindingJson) {
  return ERecord({
    id: EText(binding.id),
    slug: EText(binding.slug),
    type: EText(binding.type),
  });
}

function _getCompleteCheckoutItemsBindingsDataOrThrow(
  bindings: CheckoutItemsBindingsData | null
): {
  userItemsProductFieldBinding: BindingJson;
  userItemsProductNameFieldBinding: BindingJson;
  userItemsSkuFieldBinding: BindingJson;
  userItemsSkuImageFieldBinding: BindingJson;
} {
  if (
    bindings == null ||
    bindings.userItemsSkuFieldBinding == null ||
    bindings.userItemsProductFieldBinding == null ||
    bindings.userItemsProductNameFieldBinding == null ||
    bindings.userItemsSkuImageFieldBinding == null
  ) {
    throw new Error(
      'Missing fields data that is required to create a new Cart'
    );
  }

  // @ts-expect-error - TS2322 - Type 'CheckoutItemsBindingsData' is not assignable to type '{ userItemsProductFieldBinding: BindingJson; userItemsProductNameFieldBinding: BindingJson; userItemsSkuFieldBinding: BindingJson; userItemsSkuImageFieldBinding: BindingJson; }'.
  return bindings;
}

export const buildCheckoutPaymentSummaryExpression = () =>
  EElement({
    id: 'Commerce Checkout Payment Summary Wrapper',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutPaymentSummaryWrapper')
    ),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Commerce Checkout Summary Block Header',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutSummaryBlockHeader')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Heading',
                type: qualify(Plugins.name('Basic'), Component.name('Heading')),
                data: ERecord({
                  tag: EEnum('h2'),
                  children: EList([
                    EElement({
                      id: 'String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Payment Info'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Checkout Block Content',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutBlockContent')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Commerce Checkout Row',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutRow')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Commerce Checkout Column',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutColumn')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Summary Item',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutSummaryItem')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Summary Label',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryLabel'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'String 2',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Payment Info'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Summary Flex Box Div',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryFlexBoxDiv'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText('cardProvider'),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 2',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText('cardLastFour'),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Summary Flex Box Div 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryFlexBoxDiv'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    children: EList([
                                      EElement({
                                        id: 'Block',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'cardExpiresMonth'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Block 2',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            condition: ERecord({
                                              class: ERecord({
                                                false: ERecord({
                                                  'w-condition-invisible':
                                                    ERecord({
                                                      fields: ERecord({
                                                        cardExpiresMonth:
                                                          ERecord({
                                                            exists:
                                                              EText('yes'),
                                                          }),
                                                      }),
                                                    }),
                                                }),
                                              }),
                                            }),
                                          }),
                                          children: EList([
                                            EElement({
                                              id: 'String 3',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('String')
                                              ),
                                              data: EText(' / '),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Block 3',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'cardExpiresYear'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Column 2',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutColumn')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Summary Item 2',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutSummaryItem')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Summary Label 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryLabel'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'String 4',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Billing Address'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 4',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText(
                                              'billingAddressAddressee'
                                            ),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 5',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('billingAddressLine1'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 6',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('billingAddressLine2'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Summary Flex Box Div 3',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryFlexBoxDiv'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 3',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'billingAddressCity'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 4',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'billingAddressState'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 5',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'billingAddressPostalCode'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 7',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText(
                                              'billingAddressCountry'
                                            ),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildPaypalCheckoutPaymentSummaryExpression = () =>
  EElement({
    id: 'Commerce Checkout Payment Summary Wrapper',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutPaymentSummaryWrapper')
    ),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Commerce Checkout Summary Block Header',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutSummaryBlockHeader')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Heading',
                type: qualify(Plugins.name('Basic'), Component.name('Heading')),
                data: ERecord({
                  tag: EEnum('h2'),
                  children: EList([
                    EElement({
                      id: 'String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Payment Info'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Checkout Block Content',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutBlockContent')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Commerce Checkout Row',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutRow')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Commerce Checkout Column',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutColumn')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Summary Item',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutSummaryItem')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Summary Label',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryLabel'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'String 2',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Payment Info'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Summary Flex Box Div',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryFlexBoxDiv'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText('cardProvider'),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 2',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText('cardLastFour'),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Summary Flex Box Div 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryFlexBoxDiv'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    children: EList([
                                      EElement({
                                        id: 'Block',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'cardExpiresMonth'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Block 2',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            condition: ERecord({
                                              class: ERecord({
                                                false: ERecord({
                                                  'w-condition-invisible':
                                                    ERecord({
                                                      fields: ERecord({
                                                        cardExpiresMonth:
                                                          ERecord({
                                                            exists:
                                                              EText('yes'),
                                                          }),
                                                      }),
                                                    }),
                                                }),
                                              }),
                                            }),
                                          }),
                                          children: EList([
                                            EElement({
                                              id: 'String 3',
                                              type: qualify(
                                                Plugins.name('Basic'),
                                                Component.name('String')
                                              ),
                                              data: EText(' / '),
                                            }),
                                          ]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Block 3',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('Block')
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'cardExpiresYear'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Column 2',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutColumn')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Summary Item 2',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutSummaryItem')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Commerce Checkout Summary Label 2',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryLabel'
                                    )
                                  ),
                                  data: ERecord({
                                    children: EList([
                                      EElement({
                                        id: 'String 4',
                                        type: qualify(
                                          Plugins.name('Basic'),
                                          Component.name('String')
                                        ),
                                        data: EText('Billing Address'),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 4',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText(
                                              'billingAddressAddressee'
                                            ),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 5',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('billingAddressLine1'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 6',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('billingAddressLine2'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Commerce Checkout Summary Flex Box Div 3',
                                  type: qualify(
                                    Plugins.name('Commerce'),
                                    Component.name(
                                      'CommerceCheckoutSummaryFlexBoxDiv'
                                    )
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    children: EList([
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 3',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'billingAddressCity'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 4',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          tag: EEnum('div'),
                                          text: EBoolean(true),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'billingAddressState'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                      EElement({
                                        id: 'Commerce Checkout Summary Text Spacing On Div 5',
                                        type: qualify(
                                          Plugins.name('Commerce'),
                                          Component.name(
                                            'CommerceCheckoutSummaryTextSpacingOnDiv'
                                          )
                                        ),
                                        data: ERecord({
                                          text: EBoolean(true),
                                          tag: EEnum('div'),
                                          dyn: ERecord({
                                            bind: ERecord({
                                              innerHTML: EList([
                                                ERecord({
                                                  slug: EText(
                                                    'billingAddressPostalCode'
                                                  ),
                                                  type: EText('PlainText'),
                                                }),
                                              ]),
                                            }),
                                          }),
                                          children: EList([]),
                                        }),
                                      }),
                                    ]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 7',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText(
                                              'billingAddressCountry'
                                            ),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildCheckoutShippingSummaryExpression = () => {
  return EElement({
    id: 'Commerce Checkout Shipping Summary Wrapper',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutShippingSummaryWrapper')
    ),
    data: ERecord({
      dyn: ERecord({
        condition: ERecord({
          class: ERecord({
            false: ERecord({
              'w-condition-invisible': ERecord({
                fields: ERecord({
                  requiresShipping: ERecord({
                    eq: EText('true'),
                  }),
                }),
              }),
            }),
          }),
        }),
      }),
      children: EList([
        EElement({
          id: 'Commerce Checkout Summary Block Header',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutSummaryBlockHeader')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Heading',
                type: qualify(Plugins.name('Basic'), Component.name('Heading')),
                data: ERecord({
                  tag: EEnum('h2'),
                  children: EList([
                    EElement({
                      id: 'String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Shipping Method'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Checkout Block Content',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutBlockContent')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Commerce Checkout Row',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutRow')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Commerce Checkout Column',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutColumn')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Checkout Summary Item',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name('CommerceCheckoutSummaryItem')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'Block 1',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            slug: EText('shippingMethodName'),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                                EElement({
                                  id: 'Block 2',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('Block')
                                  ),
                                  data: ERecord({
                                    tag: EEnum('div'),
                                    text: EBoolean(true),
                                    dyn: ERecord({
                                      bind: ERecord({
                                        innerHTML: EList([
                                          ERecord({
                                            id: EText('id'),
                                            slug: EText(
                                              'shippingMethodDescription'
                                            ),
                                            type: EText('PlainText'),
                                          }),
                                        ]),
                                      }),
                                    }),
                                    children: EList([]),
                                  }),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });
};

export const buildCheckoutOrderSummaryExpression = () =>
  EElement({
    id: 'Commerce Checkout Order Summary Wrapper',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCheckoutOrderSummaryWrapper')
    ),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Commerce Checkout Summary Block Header',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutSummaryBlockHeader')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Order Summary',
                type: qualify(Plugins.name('Basic'), Component.name('Heading')),
                data: ERecord({
                  tag: EEnum('h2'),
                  children: EList([
                    EElement({
                      id: 'String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Order Summary'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Checkout Block Content',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCheckoutBlockContent')
          ),
          data: ERecord({
            children: EList([
              EElement({
                id: 'Commerce Checkout Summary Line Item',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutSummaryLineItem')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Block',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('Block')
                      ),
                      data: ERecord({
                        tag: EEnum('div'),
                        text: EBoolean(true),
                        children: EList([
                          EElement({
                            id: 'String 2',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('String')
                            ),
                            data: EText('Subtotal'),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Block 2',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('Block')
                      ),
                      data: ERecord({
                        tag: EEnum('div'),
                        text: EBoolean(true),
                        dyn: ERecord({
                          bind: ERecord({
                            innerHTML: EList([
                              ERecord({
                                slug: EText('subtotal'),
                                type: EText('CommercePrice'),
                              }),
                            ]),
                          }),
                        }),
                        children: EList([]),
                      }),
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Checkout Order Summary Extra Items List',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutOrderSummaryExtraItemsList')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Commerce Checkout Order Summary Extra Items List Item',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name(
                          'CommerceCheckoutOrderSummaryExtraItemsListItem'
                        )
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Block 3',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('Block')
                            ),
                            data: ERecord({
                              tag: EEnum('div'),
                              text: EBoolean(true),
                              dyn: ERecord({
                                bind: ERecord({
                                  innerHTML: EList([
                                    ERecord({
                                      slug: EText('name'),
                                      type: EText('PlainText'),
                                    }),
                                  ]),
                                }),
                              }),
                              children: EList([]),
                            }),
                          }),
                          EElement({
                            id: 'Block 4',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('Block')
                            ),
                            data: ERecord({
                              tag: EEnum('div'),
                              text: EBoolean(true),
                              dyn: ERecord({
                                bind: ERecord({
                                  innerHTML: EList([
                                    ERecord({
                                      slug: EText('price'),
                                      type: EText('CommercePrice'),
                                    }),
                                  ]),
                                }),
                              }),
                              children: EList([]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Checkout Summary Line Item 2',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCheckoutSummaryLineItem')
                ),
                data: ERecord({
                  children: EList([
                    EElement({
                      id: 'Block 5',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('Block')
                      ),
                      data: ERecord({
                        tag: EEnum('div'),
                        text: EBoolean(true),
                        children: EList([
                          EElement({
                            id: 'String 3',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('String')
                            ),
                            data: EText('Total'),
                          }),
                        ]),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Checkout Summary Total',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutSummaryTotal')
                      ),
                      data: ERecord({
                        tag: EEnum('div'),
                        text: EBoolean(true),
                        dyn: ERecord({
                          bind: ERecord({
                            innerHTML: EList([
                              ERecord({
                                slug: EText('total'),
                                type: EText('CommercePrice'),
                              }),
                            ]),
                          }),
                        }),
                        children: EList([]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildQuickCheckoutActionsExpression = () =>
  EElement({
    id: 'Commerce Cart Quick Checkout Actions',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommerceCartQuickCheckoutActions')
    ),
    data: ERecord({
      commerce: ERecord({
        type: EText('cart-quick-checkout-actions'),
        defaultApplePay: EBoolean(true),
      }),
      temp: ERecord({
        state: EText(QUICK_CHECKOUT_STATES.APPLE_PAY),
      }),
      children: EList([
        EElement({
          id: 'Commerce Cart Apple Pay Button',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCartApplePayButton')
          ),
          data: ERecord({
            attr: ERecord({
              [DATA_ATTR_NODE_TYPE]: EText('commerce-cart-apple-pay-button'),
              role: EText('button'),
              'aria-label': EText('Apple Pay'),
              'aria-haspopup': EText('dialog'),
            }),
            children: EList([
              EElement({
                id: 'Commerce Cart Apple Pay Icon',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceCartApplePayIcon')
                ),
                data: ERecord({}),
              }),
            ]),
          }),
        }),
        EElement({
          id: 'Commerce Cart Quick Checkout Button',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceCartQuickCheckoutButton')
          ),
          data: ERecord({
            attr: ERecord({
              [DATA_ATTR_NODE_TYPE]: EText(
                'commerce-cart-quick-checkout-button'
              ),
              role: EText('button'),
              tabindex: EText('0'),
              'aria-haspopup': EText('dialog'),
            }),
            children: EList([
              EElement({
                id: 'Commerce Quick Checkout Google Icon',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceQuickCheckoutGoogleIcon')
                ),
                data: ERecord({}),
              }),
              EElement({
                id: 'Commerce Quick Checkout Microsoft Icon',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceQuickCheckoutMicrosoftIcon')
                ),
                data: ERecord({}),
              }),
              EElement({
                id: 'Block',
                type: qualify(Plugins.name('Basic'), Component.name('Block')),
                data: ERecord({
                  tag: EEnum('div'),
                  text: EBoolean(true),
                  children: EList([
                    EElement({
                      id: 'String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText('Pay with browser.'),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildPaypalCheckoutForm = ({
  collectionState,
  betaFeatures,
}: {
  collectionState: CollectionState;
  betaFeatures: BetaFeatureFlags | null;
}) =>
  EElement({
    id: 'Commerce Paypal Checkout Form Container',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommercePaypalCheckoutFormContainer')
    ),
    data: ERecord({
      temp: ERecord({
        requiresShipping: EEnum(ORDER_TYPE.REQUIRE_SHIPPING),
        state: EEnum(CHECKOUT_STATES.DEFAULT),
        previewItems: ENumber(PREVIEW_ITEMS_DEFAULT),
        note: ELiteral({
          name: qualify(Plugins.name('Commerce'), typeName('DismissibleNote')),
          value: false,
        }),
        [BILLING_ADDRESS_TOGGLE_KEY]: EBoolean(false),
      }),
      children: EList([
        EElement({
          id: 'Commerce Layout Container',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceLayoutContainer')
          ),
          data: ERecord({
            tag: EEnum('div'),
            children: EList([
              EElement({
                id: 'Commerce Layout Main',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutMain')
                ),
                data: ERecord({
                  children: EList([
                    /* Shipping Methods */
                    buildShippingMethodsListExpression(),
                    /* Customer Info */
                    buildCheckoutCustomerInfoSummaryExpression(),
                    /* Payment Info */
                    buildPaypalCheckoutPaymentSummaryExpression(),
                    /* Order Items */
                    buildCheckoutItemsSummaryExpression({
                      bindings:
                        buildOrderItemsBindingsFromCollectionState(
                          collectionState
                        ),
                      betaFeatures,
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Layout Sidebar',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutSidebar')
                ),
                data: ERecord({
                  children: EList([
                    /* Order Summary */
                    buildCheckoutOrderSummaryExpression(),
                    EElement({
                      id: 'Commerce Checkout Place Order Button',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutPlaceOrderButton')
                      ),
                      data: ERecord({
                        attr: ERecord({
                          href: EText('#'),
                          value: EText('Place Order'),
                        }),
                        commerce: ERecord({
                          [LOADING_TEXT]: EText(
                            CHECKOUT_PLACE_ORDER_LOADING_TEXT_DEFAULT
                          ),
                          type: EText('place-order-button'),
                        }),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Paypal Checkout Error State',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommercePaypalCheckoutErrorState')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Paypal Checkout Error Message',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name(
                                'CommercePaypalCheckoutErrorMessage'
                              )
                            ),
                            data: ERecord({
                              temp: ERecord({index: ENumber(0)}),
                              texts: EList([
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.INFO.id),
                                  name: EText(CHECKOUT_ERRORS.INFO.name),
                                  copy: EText(CHECKOUT_ERRORS.INFO.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.INFO.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.SHIPPING.id),
                                  name: EText(CHECKOUT_ERRORS.SHIPPING.name),
                                  copy: EText(CHECKOUT_ERRORS.SHIPPING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.SHIPPING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.EXTRAS.id),
                                  name: EText(CHECKOUT_ERRORS.EXTRAS.name),
                                  copy: EText(CHECKOUT_ERRORS.EXTRAS.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.EXTRAS.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PRICING.id),
                                  name: EText(CHECKOUT_ERRORS.PRICING.name),
                                  copy: EText(CHECKOUT_ERRORS.PRICING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PRICING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PRODUCT.id),
                                  name: EText(CHECKOUT_ERRORS.PRODUCT.name),
                                  copy: EText(CHECKOUT_ERRORS.PRODUCT.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PRODUCT.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PAYMENT.id),
                                  name: EText(CHECKOUT_ERRORS.PAYMENT.name),
                                  copy: EText(CHECKOUT_ERRORS.PAYMENT.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PAYMENT.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.BILLING.id),
                                  name: EText(CHECKOUT_ERRORS.BILLING.name),
                                  copy: EText(CHECKOUT_ERRORS.BILLING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.BILLING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.MINIMUM.id),
                                  name: EText(CHECKOUT_ERRORS.MINIMUM.name),
                                  copy: EText(CHECKOUT_ERRORS.MINIMUM.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.MINIMUM.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.category
                                      .label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.category
                                      .label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT
                                      .category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.id
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.name
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.name
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.category
                                      .label
                                  ),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });

export const buildPaypalCheckoutFormExpressionBackend = ({
  productCollection,
  skuCollection,
  betaFeatures,
}: {
  productCollection: BackendCollection;
  skuCollection: BackendCollection;
  betaFeatures: BetaFeatureFlags | null;
}) =>
  EElement({
    id: 'Commerce Paypal Checkout Form Container',
    type: qualify(
      Plugins.name('Commerce'),
      Component.name('CommercePaypalCheckoutFormContainer')
    ),
    data: ERecord({
      temp: ERecord({
        requiresShipping: EEnum(ORDER_TYPE.REQUIRE_SHIPPING),
        state: EEnum(CHECKOUT_STATES.DEFAULT),
        previewItems: ENumber(PREVIEW_ITEMS_DEFAULT),
        note: ELiteral({
          name: qualify(Plugins.name('Commerce'), typeName('DismissibleNote')),
          value: false,
        }),
        [BILLING_ADDRESS_TOGGLE_KEY]: EBoolean(false),
      }),
      children: EList([
        EElement({
          id: 'Commerce Layout Container',
          type: qualify(
            Plugins.name('Commerce'),
            Component.name('CommerceLayoutContainer')
          ),
          data: ERecord({
            tag: EEnum('div'),
            children: EList([
              EElement({
                id: 'Commerce Layout Main',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutMain')
                ),
                data: ERecord({
                  children: EList([
                    /* Shipping Methods */
                    buildShippingMethodsListExpression(),
                    /* Customer Info */
                    buildCheckoutCustomerInfoSummaryExpression(),
                    /* Payment Info */
                    buildPaypalCheckoutPaymentSummaryExpression(),
                    /* Order Items */
                    buildCheckoutItemsSummaryExpression({
                      bindings:
                        buildCheckoutItemsBindingsFromBackendCollections(
                          productCollection,
                          skuCollection
                        ),
                      betaFeatures,
                    }),
                  ]),
                }),
              }),
              EElement({
                id: 'Commerce Layout Sidebar',
                type: qualify(
                  Plugins.name('Commerce'),
                  Component.name('CommerceLayoutSidebar')
                ),
                data: ERecord({
                  children: EList([
                    /* Order Summary */
                    buildCheckoutOrderSummaryExpression(),
                    EElement({
                      id: 'Commerce Checkout Place Order Button',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommerceCheckoutPlaceOrderButton')
                      ),
                      data: ERecord({
                        attr: ERecord({
                          href: EText('#'),
                          value: EText('Place Order'),
                        }),
                        commerce: ERecord({
                          [LOADING_TEXT]: EText(
                            CHECKOUT_PLACE_ORDER_LOADING_TEXT_DEFAULT
                          ),
                          type: EText('place-order-button'),
                        }),
                      }),
                    }),
                    EElement({
                      id: 'Commerce Paypal Checkout Error State',
                      type: qualify(
                        Plugins.name('Commerce'),
                        Component.name('CommercePaypalCheckoutErrorState')
                      ),
                      data: ERecord({
                        children: EList([
                          EElement({
                            id: 'Commerce Paypal Checkout Error Message',
                            type: qualify(
                              Plugins.name('Commerce'),
                              Component.name(
                                'CommercePaypalCheckoutErrorMessage'
                              )
                            ),
                            data: ERecord({
                              temp: ERecord({index: ENumber(0)}),
                              texts: EList([
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.INFO.id),
                                  name: EText(CHECKOUT_ERRORS.INFO.name),
                                  copy: EText(CHECKOUT_ERRORS.INFO.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.INFO.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.SHIPPING.id),
                                  name: EText(CHECKOUT_ERRORS.SHIPPING.name),
                                  copy: EText(CHECKOUT_ERRORS.SHIPPING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.SHIPPING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.EXTRAS.id),
                                  name: EText(CHECKOUT_ERRORS.EXTRAS.name),
                                  copy: EText(CHECKOUT_ERRORS.EXTRAS.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.EXTRAS.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PRICING.id),
                                  name: EText(CHECKOUT_ERRORS.PRICING.name),
                                  copy: EText(CHECKOUT_ERRORS.PRICING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PRICING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PRODUCT.id),
                                  name: EText(CHECKOUT_ERRORS.PRODUCT.name),
                                  copy: EText(CHECKOUT_ERRORS.PRODUCT.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PRODUCT.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.PAYMENT.id),
                                  name: EText(CHECKOUT_ERRORS.PAYMENT.name),
                                  copy: EText(CHECKOUT_ERRORS.PAYMENT.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.PAYMENT.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.BILLING.id),
                                  name: EText(CHECKOUT_ERRORS.BILLING.name),
                                  copy: EText(CHECKOUT_ERRORS.BILLING.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.BILLING.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(CHECKOUT_ERRORS.MINIMUM.id),
                                  name: EText(CHECKOUT_ERRORS.MINIMUM.name),
                                  copy: EText(CHECKOUT_ERRORS.MINIMUM.copy),
                                  category: EText(
                                    CHECKOUT_ERRORS.MINIMUM.category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.INVALID_DISCOUNT.category
                                      .label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.EXPIRED_DISCOUNT.category
                                      .label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.id
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.name
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.copy
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT
                                      .category.label
                                  ),
                                }),
                                ERecord({
                                  id: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.id
                                  ),
                                  copy: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.name
                                  ),
                                  name: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.name
                                  ),
                                  category: EText(
                                    CHECKOUT_ERRORS
                                      .REQUIREMENTS_NOT_MET_DISCOUNT.category
                                      .label
                                  ),
                                }),
                              ]),
                            }),
                          }),
                        ]),
                      }),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        }),
      ]),
    }),
  });
